// React Query hooks for exhibitions (F-B). Mirrors useUsers.ts shape.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArchiveSummary,
  Exhibition,
  ExhibitionCreatePayload,
  ExhibitionDeleteResponse,
  ExhibitionImageUploadResponse,
  ExhibitionListItem,
  ExhibitionState,
  ExhibitionUpdatePayload,
  BatchImportResult,
  BatchValidationReport,
  archiveExhibition,
  batchImportExhibits,
  batchValidateExhibits,
  createExhibition,
  deleteExhibition,
  getStaffExhibition,
  listExhibitions,
  listZonesInExhibition,
  reorderZonesInExhibition,
  ZoneInExhibition,
  purgeExhibition,
  regenerateExhibitionCode,
  restoreExhibition,
  transitionExhibition,
  updateExhibition,
  uploadExhibitionImage,
} from "../exhibitions";
import type { ListStatus } from "../listStatus";

export const exhibitionKeys = {
  all: ["exhibitions"] as const,
  list: (includeArchived: boolean = false) =>
    [...exhibitionKeys.all, "list", { includeArchived }] as const,
  detail: (id: string) => [...exhibitionKeys.all, "detail", id] as const,
  zones: (id: string) => [...exhibitionKeys.all, "zones", id] as const,
};

export function useExhibitions(
  options: { includeArchived?: boolean; status?: ListStatus } = {},
) {
  const includeArchived = options.includeArchived ?? false;
  const status = options.status;
  return useQuery<ExhibitionListItem[]>({
    queryKey: [
      ...exhibitionKeys.list(includeArchived),
      { status: status ?? null },
    ],
    queryFn: () => listExhibitions({ includeArchived, status }),
  });
}

export function useZonesInExhibition(exhibitionId: string | undefined) {
  return useQuery({
    queryKey: exhibitionKeys.zones(exhibitionId ?? ""),
    queryFn: () => listZonesInExhibition(exhibitionId as string),
    enabled: Boolean(exhibitionId),
  });
}

export function useExhibition(exhibitionId: string | undefined) {
  return useQuery<Exhibition>({
    queryKey: exhibitionKeys.detail(exhibitionId ?? ""),
    queryFn: () => getStaffExhibition(exhibitionId as string),
    enabled: Boolean(exhibitionId),
  });
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>): void {
  qc.invalidateQueries({ queryKey: exhibitionKeys.all });
}

export function useCreateExhibition() {
  const qc = useQueryClient();
  return useMutation<Exhibition, Error, ExhibitionCreatePayload>({
    mutationFn: createExhibition,
    onSuccess: () => invalidateAll(qc),
  });
}

/** Zone ordering (2026-09-07): saves immediately (the zones table isn't part
 *  of the form's Save) and swaps the server's ordered list into the cache. */
export function useReorderZonesInExhibition() {
  const qc = useQueryClient();
  return useMutation<
    ZoneInExhibition[],
    Error,
    { exhibitionId: string; zoneIds: string[] }
  >({
    mutationFn: ({ exhibitionId, zoneIds }) =>
      reorderZonesInExhibition(exhibitionId, zoneIds),
    onSuccess: (ordered, { exhibitionId }) => {
      qc.setQueryData(exhibitionKeys.zones(exhibitionId), ordered);
    },
  });
}

export function useUpdateExhibition() {
  const qc = useQueryClient();
  return useMutation<
    Exhibition,
    Error,
    { exhibitionId: string; payload: ExhibitionUpdatePayload }
  >({
    mutationFn: ({ exhibitionId, payload }) =>
      updateExhibition(exhibitionId, payload),
    onSuccess: (updated) => {
      invalidateAll(qc);
      qc.setQueryData(exhibitionKeys.detail(updated.id), updated);
    },
  });
}

export function useDeleteExhibition() {
  const qc = useQueryClient();
  return useMutation<ExhibitionDeleteResponse, Error, string>({
    mutationFn: deleteExhibition,
    onSuccess: () => invalidateAll(qc),
  });
}

export function useTransitionExhibition() {
  const qc = useQueryClient();
  return useMutation<
    Exhibition,
    Error,
    { exhibitionId: string; toState: ExhibitionState }
  >({
    mutationFn: ({ exhibitionId, toState }) =>
      transitionExhibition(exhibitionId, toState),
    onSuccess: (updated) => {
      invalidateAll(qc);
      qc.setQueryData(exhibitionKeys.detail(updated.id), updated);
    },
  });
}

export function useRegenerateExhibitionCode() {
  const qc = useQueryClient();
  return useMutation<{ code: string }, Error, string>({
    mutationFn: regenerateExhibitionCode,
    onSuccess: () => invalidateAll(qc),
  });
}

export function useArchiveExhibition() {
  const qc = useQueryClient();
  return useMutation<ArchiveSummary, Error, string>({
    mutationFn: archiveExhibition,
    onSuccess: (summary) => {
      invalidateAll(qc);
      qc.setQueryData(
        exhibitionKeys.detail(summary.exhibition.id),
        summary.exhibition,
      );
      // Children (zones + exhibits) cascaded — drop their caches so the
      // staff dashboard re-fetches.
      qc.invalidateQueries({ queryKey: ["zones"] });
      qc.invalidateQueries({ queryKey: ["exhibits"] });
    },
  });
}

export function useRestoreExhibition() {
  const qc = useQueryClient();
  return useMutation<ArchiveSummary, Error, string>({
    mutationFn: restoreExhibition,
    onSuccess: (summary) => {
      invalidateAll(qc);
      qc.setQueryData(
        exhibitionKeys.detail(summary.exhibition.id),
        summary.exhibition,
      );
      qc.invalidateQueries({ queryKey: ["zones"] });
      qc.invalidateQueries({ queryKey: ["exhibits"] });
    },
  });
}

export function useUploadExhibitionImage() {
  const qc = useQueryClient();
  return useMutation<
    ExhibitionImageUploadResponse,
    Error,
    { exhibitionId: string; file: File }
  >({
    mutationFn: ({ exhibitionId, file }) =>
      uploadExhibitionImage(exhibitionId, file),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: exhibitionKeys.all });
      qc.invalidateQueries({
        queryKey: exhibitionKeys.detail(vars.exhibitionId),
      });
    },
  });
}

export function usePurgeExhibition() {
  const qc = useQueryClient();
  return useMutation<
    ArchiveSummary,
    Error,
    { exhibitionId: string; reason: string }
  >({
    mutationFn: ({ exhibitionId, reason }) =>
      purgeExhibition(exhibitionId, reason),
    onSuccess: (summary) => {
      invalidateAll(qc);
      qc.setQueryData(
        exhibitionKeys.detail(summary.exhibition.id),
        summary.exhibition,
      );
      qc.invalidateQueries({ queryKey: ["zones"] });
      qc.invalidateQueries({ queryKey: ["exhibits"] });
      qc.invalidateQueries({ queryKey: ["assets"] });
    },
  });
}

// ── F-N batch exhibit upload ─────────────────────────────────────────

export function useBatchValidateExhibits() {
  return useMutation<
    BatchValidationReport,
    Error,
    { exhibitionId: string; workbook: File; files: File[] }
  >({
    mutationFn: ({ exhibitionId, workbook, files }) =>
      batchValidateExhibits(exhibitionId, workbook, files),
  });
}

export function useBatchImportExhibits() {
  const qc = useQueryClient();
  return useMutation<
    BatchImportResult,
    Error,
    {
      exhibitionId: string;
      workbook: File;
      files: File[];
      allowDuplicates?: boolean;
    }
  >({
    mutationFn: ({ exhibitionId, workbook, files, allowDuplicates }) =>
      batchImportExhibits(exhibitionId, workbook, files, allowDuplicates),
    onSuccess: () => {
      // A batch touches zone memberships, the exhibit list, and the
      // exhibitions' zone counts — refresh all three families.
      invalidateAll(qc);
      qc.invalidateQueries({ queryKey: ["zones"] });
      qc.invalidateQueries({ queryKey: ["exhibits"] });
    },
  });
}
